<p-dialog
  [(visible)]="_visible"
  [header]="header"
  [draggable]="false"
  class="add-new-events-modal"
  [contentStyle]="{'overflow': 'visible'}"
  [modal]="true"
  [style]="{width: '688px'}"
  (onHide)="onDialogHide()">

  <form [formGroup]="createEventForm">

    <!-- Title / Task Name -->
    <div class="form-field">
      <label for="event-title">{{ isAddTask ? 'Task Name' : 'Title' }}</label>
      <ng-container *ngIf="!readonly; else titleLabel">
        <input id="event-title" type="text" pInputText formControlName="title" [readonly]="readonly" class="w-full"
          [placeholder]="isAddTask ? 'e.g. Ad Hoc Data Request' : ''"/>
      </ng-container>
      <ng-template #titleLabel>
        <div class="label-only">{{ createEventForm.get('title')?.value }}</div>
      </ng-template>
    </div>

    <!-- Description -->
    <div class="form-field">
      <label for="event-description">Description</label>
      <ng-container *ngIf="!readonly; else descriptionLabel">
        <textarea id="event-description"
          [placeholder]="isAddTask ? 'Add a short description of what needs to be done...' : EVENT_DESC_PLACEHOLDER"
          pInputTextarea formControlName="description" class="events-text-area w-full"
          [autoResize]="true"></textarea>
      </ng-container>
      <ng-template #descriptionLabel>
        <div class="label-only">{{ createEventForm.get('description')?.value }}</div>
      </ng-template>
    </div>

    <!-- Start Date and End/Due Date -->
    <div class="dates-row">
      <div class="form-field">
        <label for="start-date-calendar">Start Date</label>
        <ng-container *ngIf="!readonly; else startDateLabel">
          <p-calendar
            formControlName="startDate"
            icon="pi pi-calendar"
            appendTo="body"
            [showIcon]="true"
            inputId="start-date-calendar"
            placeholder="Select date"
            (onSelect)="onDateChanged('start')"
            [ngClass]="{
              'invalid-date': createEventForm.get('startDate')?.invalid && (createEventForm.get('startDate')?.touched || createEventForm.get('startDate')?.dirty)
            }">
          </p-calendar>
        </ng-container>
        <ng-template #startDateLabel>
          <div class="label-only">{{ createEventForm.get('startDate')?.value | date:'mediumDate' }}</div>
        </ng-template>
      </div>
      <div class="form-field">
        <label for="end-date-calendar">{{ isAddTask ? 'Due Date' : 'End Date' }}</label>
        <ng-container *ngIf="!readonly; else endDateLabel">
          <p-calendar
            formControlName="endDate"
            appendTo="body"
            [showIcon]="true"
            icon="pi pi-calendar"
            [minDate]="createEventForm.value.startDate || minDateFromDb"
            inputId="end-date-calendar"
            placeholder="Select date"
            (onSelect)="onDateChanged('end')"
            [ngClass]="{
              'invalid-date': createEventForm.get('endDate')?.invalid && (createEventForm.get('endDate')?.touched || createEventForm.get('endDate')?.dirty)
            }">
          </p-calendar>
        </ng-container>
        <ng-template #endDateLabel>
          <div class="label-only">{{ createEventForm.get('endDate')?.value | date:'mediumDate' }}</div>
        </ng-template>
      </div>
    </div>

    <div class="date-note" *ngIf="!isAddTask">*Start and End Date may be the same day</div>
    <div *ngIf="createEventForm.get('endDate')?.invalid && (createEventForm.get('endDate')?.touched || createEventForm.get('endDate')?.dirty)" class="error-message">
      * {{ isAddTask ? 'Due date' : 'End date' }} is required and must be the same as or after the start date.
    </div>

    <!-- Team Assignment + Select Quarter -->
    <div class="team-quarter-row">
      <div class="form-field" *ngIf="!readonly">
        <label for="team-assignment-dropdown">Team Assignment</label>
        <app-qep-dropdown
            id="team-assignment-dropdown"
            [multi]="true"
            [options]="teamOptions"
            optionLabel="label"
            type="outlined"
            [selectAllLabel]="'All Users'"
            formControlName="teamAssignment"
            placeholder="Select team(s)..."
            class="w-full"
            [showToggleAll]="true"
            display="chip"
            [maxSelectedLabels]="isAddTask ? 1 : 3"
            selectedItemsLabel="+{0} more">
        </app-qep-dropdown>
      </div>
      <div class="form-field" *ngIf="isAddTask && !hasSelectedEvent">
        <label for="quarter-dropdown">Select Quarter</label>
        <ng-container *ngIf="!readonly; else selectQuarterLabel">
          <app-qep-dropdown
            id="quarter-dropdown"
            [options]="quartersOfYear"
            optionLabel="label"
            type="outlined"
            formControlName="quarter"
            placeholder="Select Quarter"
            class="w-full">
          </app-qep-dropdown>
        </ng-container>
        <ng-template #selectQuarterLabel>
          <div class="label-only">{{ createEventForm.get('quarter')?.value | date:'mediumDate' }}</div>
        </ng-template>
      </div>
    </div>

    <!-- Task Type / Event Type + Association (inline when Others) -->
    <div class="flex gap-3">
      <div class="form-field flex-1">
        <label for="event-type-dropdown">{{ isAddTask ? 'Task Type' : 'Event Type' }}</label>
        <ng-container *ngIf="!readonly; else eventTypeLabel">
          <app-qep-dropdown
            id="event-type-dropdown"
            [options]="investmentTypes"
            optionLabel="label"
            type="outlined"
            formControlName="eventType"
            [placeholder]="isAddTask ? 'Select type...' : 'Select an Event Type'"
            class="w-full">
          </app-qep-dropdown>
        </ng-container>
        <ng-template #eventTypeLabel>
          <div class="label-only">{{ createEventForm.get('eventType')?.value }}</div>
        </ng-template>
      </div>

      <div class="form-field flex-1" *ngIf="createEventForm.get('eventType')?.value === 'Others'">
        <label for="association-dropdown">Association</label>
        <ng-container *ngIf="!readonly; else associationLabel">
          <app-qep-dropdown
            id="association-dropdown"
            [options]="associationOptions"
            optionLabel="label"
            type="outlined"
            formControlName="association"
            placeholder="Select association..."
            class="w-full">
          </app-qep-dropdown>
        </ng-container>
        <ng-template #associationLabel>
          <div class="label-only">{{ createEventForm.get('association')?.value }}</div>
        </ng-template>
        <div *ngIf="createEventForm.get('association')?.invalid && createEventForm.get('association')?.touched" class="error-message">
          * Association is required when {{ isAddTask ? 'Task Type' : 'Event Type' }} is Others.
        </div>
      </div>
    </div>

    <!-- Portfolio Companies Tracked -->
    <div class="form-field">
      <label>Portfolio Companies Tracked</label>
      <ng-container *ngIf="hasSelectedEvent; else pcLabelText">
        <small class="field-hint">Only selected PCs count {{selectedPcCount}}</small>
      </ng-container>
       <ng-template #pcLabelText>
         <small class="field-hint">Optional - tag PCs this task applies.</small>
       </ng-template>
      <ng-container *ngIf="!readonly; else portfolioCompaniesLabel">
        <app-qep-dropdown
            id="portfolio-companies-dropdown"
            [multi]="true"
            [searchInput]="true"
            [options]="portfolioCompanyOptions"
            optionLabel="label"
            type="outlined"
            [selectAllLabel]="'All Portfolio Companies (Active)'"
            formControlName="portfolioCompanies"
            placeholder="Select portfolio companies..."
            class="w-full"
            [showToggleAll]="true"
            display="chip"
            [maxSelectedLabels]="3"
            selectedItemsLabel="+{0} more">
        </app-qep-dropdown>
      </ng-container>
      <ng-template #portfolioCompaniesLabel>
        <div class="label-only">{{ selectedPortfolioCompanyNames }}</div>
      </ng-template>
      <div *ngIf="hasSelectedEvent && excludedPortfolioCompanies.length > 0" class="excluded-pcs">
        <span class="excluded-label">Excluded: </span>
        <span class="excluded-names">
          <ng-container *ngFor="let pc of excludedPortfolioCompanies.slice(0, 2); let last = last">
            {{ pc.label }}<ng-container *ngIf="!last"> &middot; </ng-container>
          </ng-container>
        </span>
        <a class="view-all-link" (click)="showExcludedDialog = true">View All</a>
      </div>
    </div>

    <!-- Default Alert Type -->
    <div class="form-field" formGroupName="alert">
      <label for="alert-type-dropdown">Default Alert Type</label>
      <app-qep-dropdown
          id="alert-type-dropdown"
          [options]="alertTypeOptions"
          optionLabel="label"
          type="outlined"
          formControlName="type"
          placeholder="Select an Alert Type"
          class="w-full">
      </app-qep-dropdown>
    </div>

    <!-- Footer -->
    <div class="button-section">
      <button *ngIf="isPublishEvent" type="button" class="btn-unpublish"
        (click)="unPublishEvent(selectedEvent)"
        (keydown)="unPublishEvent(selectedEvent)">Unpublish this task</button>
      <div class="button-actions">
        <i *ngIf="hasSelectedEvent && !readonly && !isPublishEvent"
           class="pi pi-trash delete-icon"
           (click)="onDelete()"
           (keydown)="onDelete()"
           title="Delete"></i>
        <app-qep-button variant="outlined" label="Cancel" (click)="emitClose()" (keydown)="emitClose()"></app-qep-button>
        <app-qep-button [disabled]="createEventForm.invalid" [label]="hasSelectedEvent ? 'Save' : (isAddTask ? 'Create Task' : 'Submit')" (click)="onSubmit()"></app-qep-button>
      </div>
    </div>

  </form>

</p-dialog>

<p-dialog
  [(visible)]="showExcludedDialog"
  header="Excluded Portfolio Companies"
  [modal]="true"
  [draggable]="false"
  [style]="{width: '420px'}"
  appendTo="body">
  <div class="excluded-popup-list">
    <div *ngFor="let pc of excludedPortfolioCompanies" class="excluded-popup-item">
      {{ pc.label }}
    </div>
  </div>
</p-dialog>
